import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { Role, RoleType } from 'api/models';
import { RoleFormValues } from 'components/organisms/module-settings/roles';
import { GENERAL_API_STALE } from 'config/query-config';
import { RolesApi } from 'data/roles';
import { useGetAllTeamsFromIndexedDb } from 'hooks/use-idb';
import { useTranslation } from 'hooks/use-translation';

export const ROLES_QUERY_KEY = 'account_roles';
export const ROLES_WITH_PLACEHOLDER_QUERY_KEY =
  'account_roles_with_placeholder';

export const useCustomerRole = (): Role => ({
  roleId: -1,
  roleName: useTranslation('customer'),
  type: RoleType.Customer,
});

export const useRolesQuery = (
  queryOptions: UseQueryOptions<
    Role[],
    unknown,
    Role[],
    typeof ROLES_QUERY_KEY
  > = {},
): UseQueryResult<Role[]> =>
  useQuery(ROLES_QUERY_KEY, () => RolesApi.findAll(), {
    staleTime: GENERAL_API_STALE,
    ...queryOptions,
  });

export const useVendorRolesQuery = (
  queryOptions: UseQueryOptions<
    Role[],
    unknown,
    Role[],
    typeof ROLES_QUERY_KEY
  > = {},
): UseQueryResult<Role[]> =>
  useRolesQuery({
    ...queryOptions,
    select: (data) => data.filter(({ type }) => type === RoleType.Vendor),
  });
export const useRolesWithPlaceholderQuery = (
  queryOptions: UseQueryOptions<
    Role[],
    unknown,
    Role[],
    typeof ROLES_WITH_PLACEHOLDER_QUERY_KEY
  > = {},
): UseQueryResult<Role[]> => {
  const teams = useGetAllTeamsFromIndexedDb();
  const roleIdsWithPlaceholder = teams.map((team) => team?.roleId);

  return useQuery(
    ROLES_WITH_PLACEHOLDER_QUERY_KEY,
    async () => {
      const roles = await RolesApi.findAll();

      return roles.filter(({ roleId }) =>
        roleIdsWithPlaceholder.includes(roleId),
      );
    },
    {
      staleTime: GENERAL_API_STALE,
      enabled: roleIdsWithPlaceholder?.length > 0,
      ...queryOptions,
    },
  );
};

const createMutationFn = ({ roleName, usedByPartners, type }: RoleFormValues) =>
  RolesApi.create({
    roleName,
    usedByPartners,
    type,
  });

const createWithDefaultPlaceholderMutationFn = ({
  roleName,
}: Pick<RoleFormValues, 'roleName'>) =>
  RolesApi.createWithDefaultPlaceholder({
    roleName,
  });

const updateMutationFn = (
  id: number,
  { roleName, usedByPartners, type }: RoleFormValues,
) => {
  return RolesApi.update(id, {
    roleName,
    usedByPartners,
    type,
  });
};

export const useCreateRoleMutation = (
  options: UseMutationOptions<Role, unknown, RoleFormValues> = {},
) => useMutation(createMutationFn, options);

export const useUpdateRoleMutation = (
  id: number,
  options: UseMutationOptions<Role, unknown, RoleFormValues> = {},
) => useMutation((formValues) => updateMutationFn(id, formValues), options);

export const useDeleteRoleMutation = (
  id: number,
  options: UseMutationOptions<unknown> = {},
) => useMutation(() => RolesApi.deleteRole(id), options);

export const useCreateRoleWithDefaultPlaceholderMutation = (
  options: UseMutationOptions<
    Role,
    unknown,
    Pick<RoleFormValues, 'roleName'>
  > = {},
) => useMutation(createWithDefaultPlaceholderMutationFn, options);

export const useMakeRolesAvailableForPartnersMutation = (): UseMutationResult<
  unknown,
  unknown,
  number[]
> => {
  return useMutation<unknown, unknown, number[]>((roleIds) =>
    RolesApi.makeRolesAvailableForPartner(roleIds),
  );
};
